<template>
  <v-container>
    <h2>{{ $tt('customBillingMenu') }}</h2>
    <v-row justify="center">
      <v-col md="6" cols="12">
        <!-- プラン情報 -->
        <v-card outlined>
          <v-card-title class="title">
            {{ $tt('customBillingPlanInfo') }}
          </v-card-title>
          <v-card-text>
            <v-sheet>
              <v-layout align-start justify-space-around>
                <v-layout align-start justify-start>
                  <h4>{{ $tt('customContractPlanTitle') }}</h4>
                  <div class="pa-0 ml-2 ">
                    {{ purchaseInfo.getI18nName() }}
                    <v-chip x-small v-if="purchaseInfo.isTrial()">
                      {{ $tt('customBillingTrial') }}
                    </v-chip>
                    <br />
                    <span v-if="purchaseInfo.doesExistsExpiredAtValue()">
                      {{ purchaseInfo.getI18nExpirationDate() }}
                    </span>
                  </div>
                </v-layout>
                <a href="https://agri-info-design.com/standard-plan/" class="text-decoration-underline ml-4">
                  {{ $tt('customComparePlansText') }}
                </a>
              </v-layout>
              <div class="mt-4" v-if="isShowSpecifiedCommercialTransactionLaw">
                <a href="https://agri-info-design.com/transactions/" target="_blank">特定商取引法に基づく表記</a>
              </div>
            </v-sheet>
            <div v-if="!showUnsubscribeButton()" class="mt-1">
              {{ $tt('customBillingBillingText1') }}
            </div>
            <v-layout align-start justify-start class="mt-4">
              <!-- プラン統一 (2026-08): 有料プランは Professional のみになったため
                   アップグレード/ダウングレード導線は撤去 -->
              <v-btn
                text
                style="text-transform: none"
                outlined
                class="ml-4"
                @click="openUnsubscribeConfirmationDialog()"
                v-if="showUnsubscribeButton()"
              >
                {{ $tt('customCancelContract') }}
              </v-btn>
            </v-layout>
            <v-divider class="mt-4" />
            <!-- 請求先メールアドレス -->
            <v-sheet class="mt-4">
              <v-layout align-center justify-start>
                <h4>{{ $tt('customBillingEmailAddressTitle') }}</h4>
                <v-btn
                  style="text-transform: none"
                  color="primary"
                  class="ml-4 text-decoration-underline"
                  text
                  outlined
                  @click="openUpdateCustomerEmail()"
                >
                  {{ $tt('customChange') }}
                </v-btn>
              </v-layout>
              <div>{{ getCustomerEmail() }}</div>
            </v-sheet>
            <v-divider class="mt-4" />
            <!-- 請求書・領収書宛名 -->
            <v-sheet class="mt-4">
              <v-layout align-center justify-start>
                <h4>{{ $tt('customInvoiceReceiptAddresseeTitle') }}</h4>
                <v-btn
                  style="text-transform: none"
                  color="primary"
                  class="ml-4 text-decoration-underline"
                  text
                  outlined
                  @click="openChangeCustomerName()"
                >
                  {{ $tt('customChange') }}
                </v-btn>
              </v-layout>
              <div>{{ getCustomerName() }}</div>
            </v-sheet>
            <v-divider class="mt-4" />
            <!-- 支払い方法 -->
            <v-sheet class="mt-4">
              <v-layout align-center justify-start>
                <h4>{{ $tt('customBillingMethod') }}</h4>
                <v-btn
                  v-if="hasProfessionalFunctionality() && isJpyCurrency()"
                  style="text-transform: none"
                  color="primary"
                  class="ml-4 text-decoration-underline"
                  text
                  outlined
                  @click="openChangePaymentMethodType()"
                >
                  {{ getChangePaymentMethodTypesBtnText() }}
                </v-btn>
              </v-layout>
              <div class="mt-1">{{ getPaymentMethodTypeName() }}</div>
            </v-sheet>

            <v-divider class="mt-4" />

            <v-simple-table v-if="showCreditCardInfo()">
              <tbody>
                <!-- クレジットカード情報 -->
                <tr>
                  <td>{{ $tt('customCreditCard') }}</td>
                  <td>
                    <div class="d-flex flex-row align-self-center">
                      <credit-card-brand-icon :credit-card="defaultCard" :width="24" class="mr-2" v-if="defaultCard" />
                      <credit-card-description :credit-card="defaultCard" v-if="defaultCard" class="pt-1 mr-2" />
                      <v-btn color="success" outlined rounded small to="/billing/methods">
                        {{ $tt('customRegistration') }}
                      </v-btn>
                    </div>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
      <!-- 支払い履歴 -->
      <v-col md="6" cols="12">
        <payment-information-component />
      </v-col>
    </v-row>

    <!-- nowLoading -->
    <now-loading-overlay-component :nowLoading="nowLoading" />

    <!-- 支払い方法変更ダイアログ -->
    <v-dialog v-model="isShowChangePaymentMethodTypeDialog" max-width="40rem">
      <v-card>
        <v-card-title />
        <v-card-text>
          <add-credit-card-component
            v-if="isPaymentMethodTypesToCard"
            :isChangePaymentMethodType="true"
            @changePaymentMethodToCard="changePaymentMethodTypesToCard($event)"
          />
          <div v-else>
            {{ $tt('customChangePaymentMethodToSendInvoiceText') }}
          </div>
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-spacer />
          <v-btn
            v-if="!isPaymentMethodTypesToCard"
            @click="changePaymentMethodTypesToCustomerBalance()"
            color="primary"
          >
            OK
          </v-btn>
          <v-btn @click="isShowChangePaymentMethodTypeDialog = false" class="ml-4" outlined text>
            {{ $tt('customCancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- クレカ登録Wizard表示 -->
    <v-dialog v-model="isShowingRegisterCreditCardDialog" max-width="40rem">
      <v-card>
        <v-alert prominent class="mb-0 pa-6">
          <div class="d-flex">
            <v-icon left large>warning</v-icon>
            <div>{{ $tt('customBillingAlertMustRegisterCard') }}</div>
          </div>
        </v-alert>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" @click="isShowingRegisterCreditCardDialog = false">{{ $tt('customCancel') }}</v-btn>
          <v-btn color="primary" to="billing/methods">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Googleでのキャンセルを促す表示 -->
    <v-dialog v-model="isShowingGooglePlayCancelDialog" max-width="40rem">
      <v-card>
        <v-alert prominent class="mb-0 pa-6">
          <div class="d-flex">
            <v-icon left large>warning</v-icon>
            <div>{{ $tt('customBillingMessageUnSubscribeGoogle') }}</div>
          </div>
        </v-alert>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="isShowingGooglePlayCancelDialog = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- アップグレード、ダウングレードの確認表示 -->
    <v-dialog v-model="isShowChangePlanConfiremationDialog" max-width="40rem">
      <v-card>
        <v-alert prominent class="mb-0 pa-6">
          <div class="d-flex">
            <v-icon v-if="isVendorGoogle()" left large>warning</v-icon>
            <v-icon v-else left large color="orange darken-3">mdi-alert-decagram</v-icon>
            <div v-if="isVendorGoogle()">
              {{ $tt('customBillingMessageSubscribeGoogle') }}
            </div>
            <div v-else>
              {{ getChangePlanText() }}
            </div>
          </div>
        </v-alert>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn v-if="!isVendorGoogle()" color="secondary" @click="isShowChangePlanConfiremationDialog = false">
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn v-if="!isVendorGoogle()" color="primary" @click="upgradeOrDownGrade()">
            OK
          </v-btn>
          <v-btn v-else color="primary" @click="isShowChangePlanConfiremationDialog = false">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Unsubscribeの確認表示 -->
    <v-dialog v-model="isShowingUnsubscribeConfirmationDialog" max-width="40rem">
      <v-card>
        <v-alert prominent class="mb-0 pa-6">
          <div class="d-flex">
            <v-icon left large color="orange darken-3">mdi-alert-decagram</v-icon>
            <div v-if="purchaseInfo.isTrial()">
              {{ $tt('customBillingMessageCancelTrial') }}
            </div>
            <div v-else>
              {{ $tt('customBillingMessageCancelStandardPlan', { date: purchaseInfo.getExpiredDateFormatted() }) }}
            </div>
          </div>
        </v-alert>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" @click="isShowingUnsubscribeConfirmationDialog = false">
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn color="primary" @click="unsubscribe()">
            {{ $tt('customBillingCancellation') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 宛名変更ダイアログ -->
    <v-dialog v-model="isShowUpdateCustomerAddressee" max-width="40rem">
      <v-form ref="form" v-model="validName" lazy-validation onsubmit="return false;">
        <v-card>
          <v-card-title>{{ $tt('customChangeAddresseeBtnText') }}</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="customerName"
              :error-messages="customerNameErrors"
              @input="$v.customerName.$touch()"
              @blur="$v.customerName.$touch()"
              required
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="secondary" @click="isShowUpdateCustomerAddressee = false">
              {{ $tt('customCancel') }}
            </v-btn>
            <v-btn color="primary" :disabled="!validName" type="submit" @click.stop="updateCustomerName">
              {{ $tt('customChange') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- 請求先メールアドレスダイアログ -->
    <v-dialog v-model="isShowUpdateCustomerEmail" max-width="40rem">
      <v-form ref="form" v-model="validEmail" lazy-validation onsubmit="return false;">
        <v-card>
          <v-card-title>{{ $tt('customChangeBillingEmailAddressText') }}</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="customerEmail"
              :error-messages="customerEmailErrors"
              @input="$v.customerEmail.$touch()"
              @blur="$v.customerEmail.$touch()"
              required
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="secondary" @click="isShowUpdateCustomerEmail = false">
              {{ $tt('customCancel') }}
            </v-btn>
            <v-btn color="primary" :disabled="!validEmail" type="submit" @click.stop="updateCustomerEmail">
              {{ $tt('customChange') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </v-container>
</template>

<script>
import AddCreditCardComponent from '@/components/billing/AddCreditCardComponent'
import CreditCardBrandIcon from '@/components/billing/CreditCardBrandIcon'
import CreditCardDescription from '@/components/billing/CreditCardDescription'
import PaymentInformationComponent from '@/components/billing/PaymentInformationComponent'
import NowLoadingOverlayComponent from '@/components/NowLoadingOverlayComponent'
import StripePaymentMethodTypes from '@/lib/stripe-payment-method-types'
import i18n from '@/lib/i18n'
import PurchaseInfo from '@/lib/purchase-info'
import storage from '@/lib/storage'
import Toast from '@/lib/toast'
import UserInfo from '@/lib/user-info'
import BillingMixin from '@/components/mixins/billing-mixin'
import StripeInfo from '@/lib/stripe-info'
import Currency from '@/lib/currency'
import { validationMixin } from 'vuelidate'
import { required, email } from 'vuelidate/lib/validators'

import * as Constants from '@/lib/constants'
import Vue from 'vue'
import VueYoutube from 'vue-youtube'

Vue.use(VueYoutube)

export default {
  name: 'BillingBilling',
  components: {
    CreditCardDescription,
    CreditCardBrandIcon,
    PaymentInformationComponent,
    NowLoadingOverlayComponent,
    AddCreditCardComponent,
  },
  mixins: [BillingMixin, validationMixin],
  validations: {
    customerName: { required },
    customerEmail: { required, email },
  },
  data() {
    return {
      purchaseInfo: storage.getPurchaseInfo(),
      nowLoading: false,
      isShowingRegisterCreditCardDialog: false,
      isShowingGooglePlayCancelDialog: false,
      isShowingUnsubscribeConfirmationDialog: false,
      isShowSpecifiedCommercialTransactionLaw: i18n.isShowSpecifiedCommercialTransactionLaw(i18n.getUserLang()),
      isShowChangePlanConfiremationDialog: false,
      isShowChangePaymentMethodTypeDialog: false,
      isShowUpdateCustomerAddressee: false,
      isShowUpdateCustomerEmail: false,
      changePlanText: '',
      customerName: '',
      customerEmail: '',
      validName: false,
      validEmail: false,
      isPaymentMethodTypesToCard: true,
    }
  },
  mounted() {
    // やめても良さそう
    // this.$store.dispatch('refreshAccessToken')
    this.$store.dispatch('getStripeInfo')
  },
  methods: {
    getChangePlanText() {
      if (this.hasProfessionalFunctionality()) {
        return i18n.tt('customDowngradeConfiremationText')
      }
      if (this.hasPlusFunctionality() || this.hasStandardFunctionality()) {
        return i18n.tt('customUpgradeConfirematoinText')
      }
    },
    isSendInvoice() {
      const purchaseInfo = this.purchaseInfo.purchaseInfo
      if (purchaseInfo.purchases.length > 0) {
        const purchase = purchaseInfo.purchases[0]
        return StripePaymentMethodTypes.isSendInvoice(purchase.collectionMethod)
      }
      return false
    },
    getPaymentMethodTypeName() {
      const purchaseInfo = this.purchaseInfo.purchaseInfo
      if (this.isVendorGoogle()) return PurchaseInfo.VENDOR_GOOGLE
      const paymentMethodType = this.getStripePaymentMethodType(purchaseInfo)
      if (paymentMethodType) return paymentMethodType.name
      return null
    },
    getStripePaymentMethodType(purchaseInfo) {
      if (purchaseInfo.purchases.length > 0) {
        const purchase = purchaseInfo.purchases[0]
        if (StripePaymentMethodTypes.isSendInvoice(purchase.collectionMethod)) {
          return StripePaymentMethodTypes.getItemByStripePaymentMethodType(StripePaymentMethodTypes.CustomerBalance)
        }
        return StripePaymentMethodTypes.getItemByStripePaymentMethodType(StripePaymentMethodTypes.Card)
      }
      return null
    },
    cannotDowngrade() {
      const purchaseInfo = this.purchaseInfo.purchaseInfo
      if (purchaseInfo.purchases.length > 0) {
        const purchase = purchaseInfo.purchases[0]
        // 銀行振込の場合はダウングレードさせない
        return StripePaymentMethodTypes.isSendInvoice(purchase.collectionMethod)
      }
      // ここはありえない
      return false
    },
    showCreditCardInfo() {
      const purchaseInfo = this.purchaseInfo.purchaseInfo
      if (this.isVendorGoogle()) return false
      const paymentMethodType = this.getStripePaymentMethodType(purchaseInfo)
      if (StripePaymentMethodTypes.isCard(paymentMethodType.value)) return true
      return false
    },
    isVendorGoogle() {
      return this.purchaseInfo.isGoogle()
    },
    hasProfessionalFunctionality() {
      return UserInfo.hasProfessionalFunctionality()
    },
    hasPlusFunctionality() {
      return UserInfo.hasPlusFunctionality()
    },
    hasStandardFunctionality() {
      return UserInfo.hasStandardFunctionality()
    },
    hasFreeFunctionality() {
      return UserInfo.hasFreeFunctionality()
    },
    getBillingVendorIconUrl(purchase) {
      const vendor = purchase.billingVendor
      if (vendor === PurchaseInfo.VENDOR_STRIPE) {
        return '/images/aid-32.png'
      }
      if (vendor === PurchaseInfo.VENDOR_GOOGLE) {
        return '/images/google-play-32.png'
      }
      return null
    },
    showUnsubscribeButton() {
      if (!this.purchaseInfo.isAutoRenewing()) {
        //自動更新じゃない->解約済み
        return false
      }
      return true
    },

    openUnsubscribeConfirmationDialog() {
      if (this.isVendorGoogle()) {
        this.isShowingGooglePlayCancelDialog = true
        return
      }
      this.isShowingUnsubscribeConfirmationDialog = true
    },
    unsubscribe() {
      this.isShowingUnsubscribeConfirmationDialog = false
      this.openNowLoading()
      const params = {
        subscriptionId: this.purchaseInfo.getSubscriptionId(),
        onSuccess: this.onUnsubscribeSuccess,
        onFailure: this.onUnsubscribeFailure,
      }
      this.$store.dispatch('unsubscribeStripeSubscription', params)
    },
    onUnsubscribeSuccess() {
      // TokenによってPermissionが変わるのでトークンを取り直す
      const params = {
        onSuccess: this.onRefreshTokenSuccessFromUnSubscribe,
      }
      this.$store.dispatch('authRefreshToken', params)
    },
    onRefreshTokenSuccessFromUnSubscribe() {
      this.closeNowLoading()
      this.forceResetPurchaseInfo()
      Toast.i18nSuccess('customBillingMessageUnsubscribe')
    },
    onUnsubscribeFailure() {
      this.closeNowLoading()
    },
    forceResetPurchaseInfo() {
      const setFunction = this.$set
      const thisPage = this
      this.$store.dispatch('authGetMe', {
        onSuccess: function() {
          thisPage.$store.dispatch('getPurchaseInfo', {
            onSuccess: function() {
              setFunction(thisPage, 'purchaseInfo', storage.getPurchaseInfo())
              // Freeの場合はFreeDashBoardに遷移
              if (UserInfo.hasFreeFunctionality()) {
                thisPage.$router.push({ name: 'FreeDashBoard' })
              }
            },
          })
        },
      })
    },
    closeNowLoading() {
      this.nowLoading = false
    },
    openNowLoading() {
      this.nowLoading = true
    },
    upgradeOrDownGrade() {
      if (this.hasProfessionalFunctionality()) {
        this.downgradeToPlus()
      }
      if (this.hasPlusFunctionality() || this.hasStandardFunctionality()) {
        this.upgradeToProfessional()
      }
    },
    upgradeToProfessional() {
      const planId = this.findPlanId(Constants.PROFESSIONAL_PLAN)
      this.changeStripePlan(planId)
    },
    downgradeToPlus() {
      const planId = this.findPlanId(Constants.PLUS_PLAN)
      this.changeStripePlan(planId)
    },
    changeStripePlan(planId) {
      this.openNowLoading()
      const params = {
        subscriptionId: this.purchaseInfo.getSubscriptionId(),
        planId: planId,
        onSuccess: this.onChangeStripePlanSuccess,
        onFailure: this.onChangeStripePlanFailure,
      }
      this.$store.dispatch('changeStripePlan', params)
    },
    onChangeStripePlanSuccess() {
      // TokenによってPermissionが変わるのでトークンを取り直す
      const params = {
        onSuccess: this.onRefreshTokenSuccessFromUnChangePlan,
      }
      this.$store.dispatch('authRefreshToken', params)
    },
    onRefreshTokenSuccessFromUnChangePlan() {
      this.$store.dispatch('getStripeInfo')
      this.isShowChangePlanConfiremationDialog = false
      this.closeNowLoading()
      Toast.i18nSuccess('customDone')
      this.forceResetPurchaseInfo()
    },
    onGetMeSuccess() {
      this.closeNowLoading()
    },
    onChangeStripePlanFailure() {
      this.closeNowLoading()
    },
    findPlanId(plan) {
      let currency = this.stripeCurrency
      this.billingPlans = this.$store.getters.billingPlans.filter(function(element) {
        if (currency) {
          if (currency === element.currency) {
            if (plan === element.plan) {
              // standardの場合はfromStandardがtrueのもののみ対象
              if (UserInfo.hasStandardFunctionality()) {
                if (element.metadata.fromStandard === 'true') {
                  return element
                }
              } else {
                if (element.metadata.fromStandard !== 'true') {
                  return element
                }
              }
            }
          }
        } else {
          if (plan === element.plan) {
            // standardの場合はfromStandardがtrueのもののみ対象
            if (UserInfo.hasStandardFunctionality()) {
              if (element.metadata.fromStandard === 'true') {
                return element
              }
            } else {
              if (element.metadata.fromStandard !== 'true') {
                return element
              }
            }
          }
        }
      })

      return this.billingPlans[0].id
    },
    getCustomerName() {
      const stripeInfo = StripeInfo.get()
      if (!stripeInfo) return i18n.tt('customNone')
      if (stripeInfo.name) {
        return stripeInfo.name
      }
      return i18n.tt('customNone')
    },
    openChangeCustomerName() {
      const stripeInfo = StripeInfo.get()
      this.customerName = stripeInfo.name
      this.isShowUpdateCustomerAddressee = true
    },
    updateCustomerName() {
      this.$v.$touch()
      if (this.$v.customerName.$error) {
        console.error('Validation Error')
        return
      }
      this.openNowLoading()
      const params = {
        name: this.customerName,
        onSuccess: this.onUpdateCustomerNameSuccess,
      }
      this.$store.dispatch('updateCustomerName', params)
    },
    onUpdateCustomerNameSuccess() {
      this.$store.dispatch('getStripeInfo')
      this.isShowUpdateCustomerAddressee = false
      this.closeNowLoading()
      Toast.i18nSuccess('customDone')
    },
    openChangePaymentMethodType() {
      this.isShowChangePaymentMethodTypeDialog = true
      this.isPaymentMethodTypesToCard = false
      if (this.isSendInvoice()) {
        this.isPaymentMethodTypesToCard = true
      }
    },
    changePaymentMethodTypesToCard(stripeAddCardRequest) {
      this.openNowLoading()
      this.changePaymentMethodTypes(stripeAddCardRequest, StripePaymentMethodTypes.Card)
    },
    changePaymentMethodTypesToCustomerBalance() {
      this.openNowLoading()
      const stripeAddCardRequest = {
        stripeToken: null,
        stripeCardName: null,
      }
      this.changePaymentMethodTypes(stripeAddCardRequest, StripePaymentMethodTypes.CustomerBalance)
    },
    changePaymentMethodTypes(stripeAddCardRequest, paymentMethodTypes) {
      const params = {
        subscriptionId: this.purchaseInfo.getSubscriptionId(),
        addCardRequest: stripeAddCardRequest,
        paymentMethodTypes: paymentMethodTypes,
        onSuccess: this.changePaymentMethodTypesSuccess,
        onFailure: this.changePaymentMethodTypesFailure,
      }
      this.$store.dispatch('changePaymentMethodTypes', params)
    },
    changePaymentMethodTypesSuccess() {
      this.$store.dispatch('getStripeInfo')
      this.isShowChangePaymentMethodTypeDialog = false
      this.closeNowLoading()
      Toast.i18nSuccess('customDone')
      this.forceResetPurchaseInfo()
    },
    changePaymentMethodTypesFailure() {
      this.closeNowLoading()
    },
    getChangePaymentMethodTypesBtnText() {
      if (this.isSendInvoice()) {
        return i18n.tt('customChangePaymentMethodsToCard')
      }
      return i18n.tt('customChangePaymentMethodsToCustomerBalance')
    },
    openUpdateCustomerEmail() {
      const stripeInfo = StripeInfo.get()
      this.customerEmail = stripeInfo.email
      this.isShowUpdateCustomerEmail = true
    },
    getCustomerEmail() {
      const stripeInfo = StripeInfo.get()
      if (!stripeInfo) return i18n.tt('customNone')
      if (stripeInfo.email) {
        return stripeInfo.email
      }
      return i18n.tt('customNone')
    },
    updateCustomerEmail() {
      this.$v.$touch()
      if (this.$v.customerEmail.$error) {
        console.error('Validation Error')
        return
      }
      this.openNowLoading()
      const params = {
        email: this.customerEmail,
        onSuccess: this.onUpdateCustomerEmailSuccess,
      }
      this.$store.dispatch('updateCustomerEmail', params)
    },
    onUpdateCustomerEmailSuccess() {
      this.$store.dispatch('getStripeInfo')
      this.isShowUpdateCustomerEmail = false
      this.closeNowLoading()
      Toast.i18nSuccess('customDone')
    },
    isJpyCurrency() {
      return this.stripeCurrency === Currency.JPY
    },
  },
  computed: {
    customerNameErrors() {
      const errors = []
      if (!this.$v.customerName.$dirty) return errors
      !this.$v.customerName.required && errors.push(this.$tt('ValidationTextRequired'))
      return errors
    },
    customerEmailErrors() {
      const errors = []
      if (!this.$v.customerEmail.$dirty) return errors
      !this.$v.customerEmail.email && errors.push(this.$tt('ValidationBadEmail'))
      !this.$v.customerEmail.required && errors.push(this.$tt('ValidationTextRequired'))
      return errors
    },
  },
}
</script>
